import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { toast } from 'react-toastify';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Undo,
  Redo,
  ImagePlus,
  Images,
  Link2,
} from 'lucide-react';
import { IframeExtension } from './IframeExtension';
import {
  is_embeddable_iframe_src,
  is_trusted_iframe_src,
  resolve_pasted_iframe_embed,
  trusted_iframe_origins,
} from '../../../lib/trustedIframeOrigins';

export type RichTextEditorHandle = {
  insertImage: (url: string, alt?: string) => void;
};

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onImageUpload?: (file: File) => Promise<string | null>;
  imageUploading?: boolean;
  /** Opens the media library so the user can pick an already-uploaded image. */
  onOpenImageLibrary?: () => void;
}

const MenuButton = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={e => {
      e.preventDefault();
      onClick();
    }}
    disabled={disabled}
    title={title}
    className={`p-2 rounded-lg transition-colors ${
      isActive ? 'bg-primary text-white' : 'hover:bg-gray-100 text-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { content, onChange, onImageUpload, imageUploading = false, onOpenImageLibrary },
    ref
  ) {
    const { t } = useTranslation('admin');
    const imageInputRef = React.useRef<HTMLInputElement>(null);

    const editor: Editor | null = useEditor({
      extensions: [
        StarterKit.configure({ link: false }),
        Image.configure({
          inline: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
        }),
        IframeExtension,
      ],
      content: content,
      editorProps: {
        handlePaste: (_, event) => {
          if (trusted_iframe_origins.size === 0) return false;

          const decision = resolve_pasted_iframe_embed(event.clipboardData);
          if (decision.action === 'ignore') return false;

          if (decision.action === 'block') {
            toast.error(
              t(
                decision.reason === 'not_embeddable'
                  ? 'blogAdmin.iframe_not_embeddable'
                  : 'blogAdmin.iframe_untrusted_origin'
              )
            );
            return true;
          }

          return (
            editor
              ?.chain()
              .focus()
              .setIframe({
                src: decision.src,
                title: decision.title ?? t('blogAdmin.default_iframe_title'),
              })
              .run() ?? false
          );
        },
      },
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        insertImage: (url: string, alt?: string) => {
          editor
            ?.chain()
            .focus()
            .setImage({ src: url, alt: alt ?? '' })
            .run();
        },
      }),
      [editor]
    );

    const handleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !editor || !onImageUpload) return;

      try {
        const imageUrl = await onImageUpload(file);
        if (imageUrl) {
          editor.chain().focus().setImage({ src: imageUrl, alt: file.name }).run();
        }
      } finally {
        event.target.value = '';
      }
    };

    const normalizeUrl = (url: string) => {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return '';
      if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmedUrl)) return trimmedUrl;
      return `https://${trimmedUrl}`;
    };

    const handleLinkClick = () => {
      if (!editor) return;

      const previousUrl = editor.getAttributes('link').href ?? '';
      const input = window.prompt(t('blogAdmin.enter_link_url', 'Enter link URL'), previousUrl);

      if (input === null) return;
      const normalizedUrl = normalizeUrl(input);
      if (!normalizedUrl) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }

      editor.chain().focus().extendMarkRange('link').setLink({ href: normalizedUrl }).run();
    };

    const handleIframeClick = () => {
      if (!editor) return;
      if (trusted_iframe_origins.size === 0) {
        toast.error(t('blogAdmin.iframe_allowlist_empty'));
        return;
      }

      const input = window.prompt(t('blogAdmin.enter_iframe_url'), '');
      if (input === null) return;

      const src = input.trim();
      if (!src) return;

      if (!is_embeddable_iframe_src(src)) {
        toast.error(
          t(
            is_trusted_iframe_src(src)
              ? 'blogAdmin.iframe_not_embeddable'
              : 'blogAdmin.iframe_untrusted_origin'
          )
        );
        return;
      }

      const title = window.prompt(t('blogAdmin.enter_iframe_title'), '')?.trim();

      editor
        .chain()
        .focus()
        .setIframe({ src, title: title || t('blogAdmin.default_iframe_title') })
        .run();
    };

    if (!editor) return null;

    return (
      <div className="border border-gray-300 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent transition-all">
        <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1">
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          >
            <Bold size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          >
            <Italic size={18} />
          </MenuButton>
          <MenuButton
            onClick={handleLinkClick}
            isActive={editor.isActive('link')}
            title={t('blogAdmin.insert_link', 'Insert link')}
          >
            <Link2 size={18} />
          </MenuButton>
          <MenuButton
            onClick={handleIframeClick}
            isActive={editor.isActive('iframe')}
            title={t('blogAdmin.insert_iframe')}
          >
            <Code size={18} />
          </MenuButton>
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
          >
            <Heading1 size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
          >
            <Heading2 size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
          >
            <Heading3 size={18} />
          </MenuButton>
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
          >
            <List size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
          >
            <ListOrdered size={18} />
          </MenuButton>
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
          >
            <Quote size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
          >
            <Code size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => imageInputRef.current?.click()}
            disabled={!onImageUpload || imageUploading}
            title={t('blogAdmin.insert_image_upload')}
          >
            <ImagePlus size={18} />
          </MenuButton>
          {onOpenImageLibrary && (
            <MenuButton
              onClick={() => onOpenImageLibrary()}
              disabled={imageUploading}
              title={t('blogAdmin.insert_image_library')}
            >
              <Images size={18} />
            </MenuButton>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageSelection}
            disabled={!onImageUpload || imageUploading}
          />
          <div className="flex-grow" />
          <MenuButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo size={18} />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo size={18} />
          </MenuButton>
        </div>
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none p-4 min-h-[400px] focus:outline-none"
        />
      </div>
    );
  }
);

export default RichTextEditor;
